import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Receipt } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { getBillingProfile, saveBillingProfile, startCheckout, formatApiError } from "@/lib/api";

const EMPTY = { customer_type: "private", first_name: "", last_name: "", legal_name: "", address: "", city: "", postal_code: "", province: "", country: "IT", vat_number: "", tax_code: "", sdi_code: "", pec: "", billing_email: "" };

export function BillingProfileForm({ onSaved, submitLabel = "Salva dati di fatturazione", compact = false }) {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  useEffect(() => { getBillingProfile().then((r) => r.profile && setF({ ...EMPTY, ...r.profile })).catch(() => {}); }, []);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const company = f.customer_type === "company";
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { const r = await saveBillingProfile(f); toast.success("Dati di fatturazione salvati"); onSaved?.(r.profile); }
    catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  const field = (k, label, props = {}) => (
    <label className="block text-xs text-slate-400 space-y-1">
      <span>{label}</span>
      <Input value={f[k] || ""} onChange={set(k)} className="bg-white/[0.03] border-white/10 text-slate-100" data-testid={`billing-${k}`} {...props} />
    </label>
  );
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="billing-profile-form">
      <div className="flex gap-2" data-testid="billing-customer-type">
        {[["private", "Privato"], ["company", "Azienda / P.IVA"]].map(([v, l]) => (
          <button type="button" key={v} onClick={() => setF({ ...f, customer_type: v })} data-testid={`billing-type-${v}`}
            className={`flex-1 rounded-xl border px-3 py-2 text-sm transition-colors ${f.customer_type === v ? "border-cyan-400/50 bg-cyan-500/10 text-cyan-200" : "border-white/10 text-slate-400 hover:border-white/20"}`}>{l}</button>
        ))}
      </div>
      {company ? (
        <>
          {field("legal_name", "Ragione sociale")}
          <div className="grid grid-cols-2 gap-3">{field("vat_number", "Partita IVA", { placeholder: "IT01234567890" })}{field("tax_code", "Codice fiscale (se diverso)")}</div>
          <div className="grid grid-cols-2 gap-3">{field("sdi_code", "Codice destinatario SDI", { placeholder: "0000000" })}{field("pec", "PEC (alternativa al codice SDI)")}</div>
        </>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3">{field("first_name", "Nome")}{field("last_name", "Cognome")}</div>
          {field("tax_code", "Codice fiscale", { placeholder: "RSSMRA80A01H501U" })}
        </>
      )}
      {field("address", "Indirizzo")}
      <div className="grid grid-cols-3 gap-3">{field("city", "Città")}{field("postal_code", "CAP")}{field("province", "Provincia", { placeholder: "MI" })}</div>
      <div className="grid grid-cols-2 gap-3">{field("country", "Paese (ISO)", { placeholder: "IT" })}{field("billing_email", "Email per le fatture (opzionale)")}</div>
      {!compact && <p className="text-[11px] text-slate-500">I dati servono per emettere la tua fattura elettronica. Prezzi IVA esclusa: l'IVA viene calcolata al pagamento.</p>}
      <Button type="submit" disabled={busy} className="w-full bg-gradient-to-r from-cyan-400 to-violet-500 text-slate-950 font-semibold" data-testid="billing-profile-submit">
        {busy ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Receipt size={14} className="mr-2" />}{submitLabel}
      </Button>
    </form>
  );
}

export default function DatiFatturazione() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const kind = params.get("kind");
  const item = params.get("item");
  const onSaved = async () => {
    if (!kind || !item) return nav("/app/impostazioni");
    try { const r = await startCheckout(kind, item); window.location.href = r.checkout_url; }
    catch (e) { toast.error(formatApiError(e, "Impossibile avviare il pagamento")); }
  };
  return (
    <div className="min-h-screen px-4 py-10 flex items-start justify-center" data-testid="billing-data-page">
      <div className="w-full max-w-lg nadia-card p-6 space-y-5">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-cyan-300">Prima del pagamento</div>
          <h1 className="font-heading text-2xl font-semibold text-white mt-1">Dati di fatturazione</h1>
          <p className="text-sm text-slate-400 mt-1">Servono per la fattura. Poi ti porto al pagamento sicuro Stripe.</p>
        </div>
        <BillingProfileForm onSaved={onSaved} submitLabel={kind ? "Salva e vai al pagamento" : "Salva"} />
        <div className="text-center text-[11px] text-slate-600">Nadia Cloud · support@nadiacloud.it</div>
      </div>
    </div>
  );
}
